// Recognition shared constants and small helpers
// Keep minimal and framework-agnostic so they can be reused across pages/components.
import { toFormattedDate } from '@/utils/date';

export const RECOGNITION_REDEMPTION_METHOD_CHOICES = [
  { text: 'Gift card', value: 'VOUCHER' },
  { text: 'Cash transferred to bank account', value: 'BANK_ACCOUNT' },
  { text: 'Cash paid into Savings pot', value: 'WPS' },
];

export const RECOGNITION_BUDGET_CADENCE_OPTIONS = [
  { text: 'Year', value: 'YEAR' },
  { text: 'Quarter', value: 'QUARTER' },
  { text: 'Month', value: 'MONTH' },
];

// Days per month helper for recognition budget/year-start logic.
// February is treated as 29 days since the exact year is irrelevant for financial year start.
export const DAYS_IN_MONTH = {
  1: 31,
  2: 29,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
};

export const MONTH_OPTIONS = [
  { text: 'January', value: 1 },
  { text: 'February', value: 2 },
  { text: 'March', value: 3 },
  { text: 'April', value: 4 },
  { text: 'May', value: 5 },
  { text: 'June', value: 6 },
  { text: 'July', value: 7 },
  { text: 'August', value: 8 },
  { text: 'September', value: 9 },
  { text: 'October', value: 10 },
  { text: 'November', value: 11 },
  { text: 'December', value: 12 },
];

export const RECOGNITION_AWARD_TYPES = {
  OPEN: 'OPEN',
  PROGRAM: 'PROGRAM',
  MILESTONE: 'MILESTONE',
};

export const RECOGNITION_APPROVAL_TYPES = {
  NONE: 'NONE',
  AMOUNT: 'AMOUNT',
  ALL: 'ALL',
};

// Milestone events
export const RECOGNITION_MILESTONE_EVENT_TYPES = {
  BIRTHDAY: 'BIRTHDAY',
  ANNIVERSARY: 'ANNIVERSARY',
};

export const RECOGNITION_MILESTONE_EVENT_OPTIONS = [
  { text: 'Birthdays', value: RECOGNITION_MILESTONE_EVENT_TYPES.BIRTHDAY },
  { text: 'Service anniversaries', value: RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY },
];

export const toMonthNumber = v => {
  if (v === null || v === undefined || v === '') {
    return undefined;
  }
  if (Number.isFinite(v)) {
    return Number(v);
  }
  const s = String(v).trim().toLowerCase();
  const found = MONTH_OPTIONS.find(m => m.text.toLowerCase() === s);
  return found ? found.value : undefined;
};

// Analytics

export const RECOGNITION_ANALYTICS_DATE_RANGES = [
  { text: 'Last 12 months', value: 'last_12_months' },
  { text: 'Last 6 months', value: 'last_6_months' },
  { text: 'Last 3 months', value: 'last_3_months' },
  { text: 'Year to date', value: 'year_to_date' },
];

// Format a Date as a local-time ISO date string (YYYY-MM-DD).
export const toIsoDateString = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Convert a date range selector value to an ISO date string.
 * Shared by TransactionsTable and AnalyticsTab.
 */
export const getDateFromForRange = rangeValue => {
  if (!rangeValue) {
    return null;
  }
  const now = new Date();

  switch (rangeValue) {
    case 'last_30_days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return toIsoDateString(d);
    }
    case 'last_90_days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 90);
      return toIsoDateString(d);
    }
    case 'last_year':
    case 'last_12_months': {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 1);
      return toIsoDateString(d);
    }
    case 'last_6_months': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 6);
      return toIsoDateString(d);
    }
    case 'last_3_months': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 3);
      return toIsoDateString(d);
    }
    case 'year_to_date': {
      return toIsoDateString(new Date(now.getFullYear(), 0, 1));
    }
    default:
      return null;
  }
};

// Forward-looking ranges for the recognition "Upcoming" milestone awards tab.
// 'custom' is paired with a date picker; the others resolve via getDateToForRange.
export const UPCOMING_AWARDS_DEFAULT_RANGE = 'next_30_days';

export const UPCOMING_AWARDS_DATE_RANGES = [
  { text: 'Next 30 days', value: 'next_30_days' },
  { text: 'Next 60 days', value: 'next_60_days' },
  { text: 'Next 90 days', value: 'next_90_days' },
  { text: 'Custom date', value: 'custom' },
];

// Number of days each non-custom upcoming range looks ahead.
export const UPCOMING_AWARDS_RANGE_DAYS = {
  next_30_days: 30,
  next_60_days: 60,
  next_90_days: 90,
};

/**
 * Convert an upcoming-awards range selector value to a forward ISO date string
 * (today + N days). Returns null for unknown/custom ranges, where the caller
 * supplies its own date.
 */
export const getDateToForRange = rangeValue => {
  const days = UPCOMING_AWARDS_RANGE_DAYS[rangeValue];
  if (!days) {
    return null;
  }
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toIsoDateString(d);
};

/**
 * One phrase for every place that names the upcoming-awards window: the funds-shortfall banner
 * ("...£X scheduled {phrase}."), the tab summary ("£X due {phrase}") and the empty state
 * ("No upcoming awards {phrase}."). It carries its own preposition so all three read the same and
 * cannot drift apart, which is how the banner ended up stuck on "in the next 30 days" (B2B-170).
 */
export const getUpcomingWindowPhrase = (rangeValue, customDate = null) => {
  if (rangeValue === 'custom') {
    return customDate ? `up to ${toFormattedDate(customDate, 'DD MMM YYYY')}` : 'in the selected period';
  }
  return `in the next ${UPCOMING_AWARDS_RANGE_DAYS[rangeValue]} days`;
};

export const toTwoDecimalsOrEmpty = v => {
  const n = parseFloat(String(v).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n)) {
    return '';
  }
  return (Math.round(n * 100) / 100).toFixed(2);
};

// Content reports
export const CONTENT_REPORT_STATUSES = {
  PENDING: 'PENDING',
  DISMISSED: 'DISMISSED',
  ACTION_TAKEN: 'ACTION_TAKEN',
};

export const CONTENT_REPORT_ACTIONS = {
  DISMISS: 'DISMISS',
  HIDE: 'HIDE',
  RESTORE: 'RESTORE',
};

export const CONTENT_REPORT_ENTITY_LABELS = {
  ec_post: 'Post',
  recognition_nomination: 'Nomination',
  recognition_grant: 'Grant',
  ec_comment: 'Comment',
};

// Content reports filter bar (Reported content tab). The status filter is
// applied client-side over the fetched list, so the values below are local
// selector tokens rather than raw API status strings (see ReportsTable.vue).
export const CONTENT_REPORT_STATUS_FILTERS = {
  ALL: null,
  PENDING: 'PENDING',
  HIDDEN: 'HIDDEN',
  // Resolved = an admin reviewed the report and took action (status ACTION_TAKEN),
  // whether they kept the content hidden or reinstated it. Without this option,
  // reinstated items (ACTION_TAKEN && !is_hidden) match no specific filter and are
  // reachable only under "All" — i.e. reviewed content would be effectively hidden.
  RESOLVED: 'RESOLVED',
  DISMISSED: 'DISMISSED',
};

export const CONTENT_REPORT_STATUS_FILTER_BAR_OPTIONS = [
  { text: 'All reports', value: CONTENT_REPORT_STATUS_FILTERS.ALL },
  { text: 'Pending', value: CONTENT_REPORT_STATUS_FILTERS.PENDING },
  { text: 'Hidden', value: CONTENT_REPORT_STATUS_FILTERS.HIDDEN },
  { text: 'Resolved', value: CONTENT_REPORT_STATUS_FILTERS.RESOLVED },
  { text: 'Dismissed', value: CONTENT_REPORT_STATUS_FILTERS.DISMISSED },
];

export const CONTENT_REPORT_TIME_RANGES = {
  ALL: null,
  TODAY: 'today',
  LAST_WEEK: 'last_week',
  LAST_MONTH: 'last_month',
};

export const CONTENT_REPORT_TIME_RANGE_OPTIONS = [
  { text: 'All time', value: CONTENT_REPORT_TIME_RANGES.ALL },
  { text: 'Today', value: CONTENT_REPORT_TIME_RANGES.TODAY },
  { text: 'Last week', value: CONTENT_REPORT_TIME_RANGES.LAST_WEEK },
  { text: 'Last month', value: CONTENT_REPORT_TIME_RANGES.LAST_MONTH },
];

export const CONTENT_REPORT_COUNT_FILTERS = {
  // 'ALL' rather than null so it stays distinct from the disabled
  // "Report number" placeholder (value null) shown as the default in the box.
  ALL: 'ALL',
  ONE: '1',
  TWO: '2',
  THREE_PLUS: '3+',
};

export const CONTENT_REPORT_COUNT_OPTIONS = [
  { text: 'All', value: CONTENT_REPORT_COUNT_FILTERS.ALL },
  { text: '1', value: CONTENT_REPORT_COUNT_FILTERS.ONE },
  { text: '2', value: CONTENT_REPORT_COUNT_FILTERS.TWO },
  { text: '3+', value: CONTENT_REPORT_COUNT_FILTERS.THREE_PLUS },
];

/**
 * Earliest `last_reported_at` (Date) to keep for a content-report time range,
 * or null when the range imposes no lower bound. Mirrors getDateFromForRange
 * but returns a Date for client-side comparison against ISO timestamps.
 */
export const getContentReportTimeRangeFrom = rangeValue => {
  const now = new Date();
  switch (rangeValue) {
    case CONTENT_REPORT_TIME_RANGES.TODAY:
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    case CONTENT_REPORT_TIME_RANGES.LAST_WEEK: {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      return d;
    }
    case CONTENT_REPORT_TIME_RANGES.LAST_MONTH: {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return d;
    }
    default:
      return null;
  }
};

export const CONTENT_REPORT_CATEGORY_LABELS = {
  harassment: 'Harassment',
  discrimination: 'Discrimination',
  spam: 'Spam',
  other: 'Other',
};

export const RECOGNITION_REPORT_SETTINGS_DEFAULTS = {
  autoHide: true,
  maxReportsPerDay: 3,
};
